import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Chip,
  Typography,
  Tooltip
} from '@mui/material';
import { Edit, Refresh, Check, Close } from '@mui/icons-material';
import api from '../../../services/api/client';

export default function PacksManagement({ onUpdate }) {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [editingPack, setEditingPack] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchPacks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/admin/credits/packs?include_inactive=true');
      setPacks(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de chargement des packs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPacks();
  }, [fetchPacks]);

  const handleEdit = (pack) => {
    setEditingPack({ ...pack });
    setEditDialog(true);
  };

  const handleSave = async () => {
    if (!editingPack) return;

    try {
      setSaving(true);
      await api.patch(`/admin/credits/packs/${editingPack.id}`, {
        name: editingPack.name,
        credits: parseInt(editingPack.credits),
        price_eur: parseFloat(editingPack.price_eur),
        validity_months: parseInt(editingPack.validity_months),
        discount_percentage: parseFloat(editingPack.discount_percentage) || 0,
        description: editingPack.description,
        is_active: editingPack.is_active,
        display_order: parseInt(editingPack.display_order) || 0
      });
      setEditDialog(false);
      setEditingPack(null);
      fetchPacks();
      onUpdate?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la mise a jour');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Gestion des Packs ({packs.length})
        </Typography>
        <Button startIcon={<Refresh />} onClick={fetchPacks}>
          Actualiser
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell>Nom</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell align="right">Credits</TableCell>
              <TableCell align="right">Prix (EUR)</TableCell>
              <TableCell align="right">Validite (mois)</TableCell>
              <TableCell align="right">Reduction (%)</TableCell>
              <TableCell align="center">Statut</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {packs.map((pack) => (
              <TableRow key={pack.id} hover>
                <TableCell>
                  <Typography fontWeight="medium">{pack.name}</Typography>
                  {pack.description && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {pack.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip label={pack.slug} size="small" />
                </TableCell>
                <TableCell align="right">
                  {pack.credits?.toLocaleString() || 0}
                </TableCell>
                <TableCell align="right">
                  {pack.price_eur?.toFixed(2) || '0.00'}
                </TableCell>
                <TableCell align="right">
                  {pack.validity_months || 12}
                </TableCell>
                <TableCell align="right">
                  {pack.discount_percentage ? (
                    <Chip
                      label={`-${pack.discount_percentage}%`}
                      color="success"
                      size="small"
                    />
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell align="center">
                  {pack.is_active ? (
                    <Chip label="Actif" color="success" size="small" />
                  ) : (
                    <Chip label="Inactif" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Modifier">
                    <IconButton size="small" onClick={() => handleEdit(pack)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier le Pack</DialogTitle>
        <DialogContent>
          {editingPack && (
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Nom"
                value={editingPack.name || ''}
                onChange={(e) => setEditingPack({ ...editingPack, name: e.target.value })}
                fullWidth
              />
              <TextField
                label="Description"
                value={editingPack.description || ''}
                onChange={(e) => setEditingPack({ ...editingPack, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
              <TextField
                label="Credits"
                type="number"
                value={editingPack.credits || 0}
                onChange={(e) => setEditingPack({ ...editingPack, credits: e.target.value })}
                fullWidth
                inputProps={{ min: 1 }}
              />
              <TextField
                label="Prix (EUR)"
                type="number"
                value={editingPack.price_eur || 0}
                onChange={(e) => setEditingPack({ ...editingPack, price_eur: e.target.value })}
                fullWidth
                inputProps={{ step: 0.01, min: 0 }}
              />
              <TextField
                label="Validite (mois)"
                type="number"
                value={editingPack.validity_months || 12}
                onChange={(e) => setEditingPack({ ...editingPack, validity_months: e.target.value })}
                fullWidth
                inputProps={{ min: 1 }}
              />
              <TextField
                label="Reduction (%)"
                type="number"
                value={editingPack.discount_percentage || 0}
                onChange={(e) => setEditingPack({ ...editingPack, discount_percentage: e.target.value })}
                fullWidth
                inputProps={{ step: 0.1, min: 0, max: 100 }}
              />
              <TextField
                label="Ordre d'affichage"
                type="number"
                value={editingPack.display_order || 0}
                onChange={(e) => setEditingPack({ ...editingPack, display_order: e.target.value })}
                fullWidth
                inputProps={{ min: 0 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editingPack.is_active || false}
                    onChange={(e) => setEditingPack({ ...editingPack, is_active: e.target.checked })}
                  />
                }
                label="Pack actif"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)} startIcon={<Close />}>
            Annuler
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <Check />}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
